import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../modal/modal.jsx';
import ProfileImagePreview from '../profile_image_preview/ProfileImagePreview.jsx';
import ApiService from '../../api_services/apiService.jsx';
import './MessageReportDetailsModal.css';

const tabs = [
    { id: 'report', label: 'Zgłoszenie' },
    { id: 'message', label: 'Wiadomość' },
    { id: 'chat', label: 'Czat' },
];

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pl-PL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const valueOrDash = (value) => (value === null || value === undefined || value === '' ? '-' : value);

const resolvePersonName = (person, fallback = '-') => {
    const nickname = person?.nickname || person?.nick || '';
    const fullName = person?.fullName
        || person?.displayName
        || [person?.name, person?.surname].filter(Boolean).join(' ')
        || '';

    if (nickname && fullName) return `${nickname} (${fullName})`;
    return nickname || fullName || fallback || '-';
};

function MessageReportDetailsModal({ isOpen, report, initialTab, onClose, onResolve, resolving }) {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [isParticipant, setIsParticipant] = useState(null);
    const [participantLoading, setParticipantLoading] = useState(false);
    const [participantError, setParticipantError] = useState(false);

    const messages = useMemo(() => report?.messages || [], [report]);
    const selectedMessage = messages.find((item) => item.messageId === selectedMessageId) || messages[0] || null;
    const message = selectedMessage?.message || null;

    useEffect(() => {
        if (!isOpen) return;
        setActiveTab(initialTab);
        setSelectedMessageId(report?.messages?.[0]?.messageId || null);
    }, [isOpen, report, initialTab]);

    useEffect(() => {
        const bunchId = report?.bunchId;
        const userId = report?.reportedUserId;
        const validBunchId = bunchId !== null && bunchId !== undefined && bunchId !== '' && bunchId !== '-';
        const validUserId = userId !== null && userId !== undefined && userId !== '' && userId !== '-';

        if (!isOpen || !validBunchId || !validUserId) {
            setIsParticipant(null);
            setParticipantError(false);
            setParticipantLoading(false);
            return undefined;
        }

        let active = true;
        setParticipantLoading(true);
        setParticipantError(false);

        ApiService.checkUserIsParticipant(bunchId, userId)
            .then((result) => {
                if (active) setIsParticipant(result);
            })
            .catch(() => {
                if (active) {
                    setIsParticipant(null);
                    setParticipantError(true);
                }
            })
            .finally(() => {
                if (active) setParticipantLoading(false);
            });

        return () => {
            active = false;
        };
    }, [isOpen, report?.bunchId, report?.reportedUserId]);

    if (!report) return null;

    const sender = report.reportSender;
    const senderName = resolvePersonName(sender, report.senderName || report.reportSenderId);
    const availableActions = report.availableActions || {};
    const reportedUser = report.reportedUser || {};
    const reportedUserName = resolvePersonName(reportedUser, report.reportedUserId);
    const canResolve = availableActions.canResolve !== false
        && report.status !== 'RESOLVED'
        && (report.reportGroupId || report.reportIds?.length);
    const contextTitle = report.groupType === 'MESSAGE'
        ? 'Szczegóły wiadomości'
        : report.groupType === 'CHAT' ? 'Szczegóły czatu' : 'Szczegóły zgłoszenia';
    const contextLabel = report.groupType === 'MESSAGE'
        ? 'ID wiadomości'
        : report.groupType === 'CHAT' ? 'ID czatu' : 'ID grupy zgłoszeń';
    const contextIdentifier = report.groupType === 'MESSAGE'
        ? report.messageId || report.groupKey
        : report.groupType === 'CHAT'
            ? report.bunchId || report.groupKey
            : report.reportGroupId || report.reportId;

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="message-report-modal">
            <div className="message-report-modal__header">
                <div>
                    <span className="message-report-modal__eyebrow">Raport wiadomości</span>
                    <h2>{contextTitle}</h2>
                    <span className="message-report-modal__identifier">{contextLabel}: {valueOrDash(contextIdentifier)}</span>
                </div>
                <span className={`message-report-status message-report-status--${String(report.status || '').toLowerCase()}`}>
                    {valueOrDash(report.status)}
                </span>
            </div>

            <div className="message-report-modal__tabs" role="tablist" aria-label="Widok raportu">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        className={activeTab === tab.id ? 'is-active' : ''}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="message-report-modal__body">
                {activeTab === 'report' && (
                    <div className="message-report-details-grid">
                        <div className="message-report-profiles message-report-details-grid__wide">
                            <div className="message-report-sender-card">
                            <ProfileImagePreview
                                profile={sender}
                                userId={report.reportSenderId}
                                name={senderName}
                                size="medium"
                            />
                            <div>
                                <span>Profil zgłaszającego</span>
                                <strong>{valueOrDash(senderName)}</strong>
                            </div>
                            </div>
                            <div className="message-report-sender-card">
                                <ProfileImagePreview
                                    profile={reportedUser}
                                    userId={report.reportedUserId}
                                    name={reportedUserName}
                                    size="medium"
                                />
                                <div>
                                    <span>Zgłoszony użytkownik</span>
                                    <strong>{valueOrDash(reportedUserName)}</strong>
                                </div>
                            </div>
                        </div>
                        <div className="message-report-participation message-report-details-grid__wide">
                            <span>Uczestnictwo zgłoszonego w paczce</span>
                            <strong className={isParticipant === true ? 'is-positive' : isParticipant === false ? 'is-negative' : ''}>
                                {participantLoading
                                    ? 'Sprawdzanie...'
                                    : participantError
                                        ? 'Nie udało się sprawdzić'
                                        : isParticipant === true
                                            ? 'Aktywny uczestnik'
                                            : isParticipant === false
                                                ? 'Nie jest aktywnym uczestnikiem'
                                                : 'Brak danych'}
                            </strong>
                        </div>
                        <div><span>Zgłaszający</span><strong>{valueOrDash(senderName)}</strong></div>
                        <div><span>Powód</span><strong>{valueOrDash(report.reason)}</strong></div>
                        <div><span>Grupa czatu</span><strong>{valueOrDash(report.bunch?.name || report.bunchId)}</strong></div>
                        <div><span>Raporty</span><strong>{valueOrDash(report.reportCount)}</strong></div>
                        <div><span>Wiadomości</span><strong>{valueOrDash(report.messageCount || report.messages?.length)}</strong></div>
                        <div><span>Ostatnie zgłoszenie</span><strong>{formatDate(report.lastReportedAt || report.createdAt)}</strong></div>
                        <div><span>Unikalni zgłaszający</span><strong>{valueOrDash(report.uniqueReporterCount)}</strong></div>
                        <div className="message-report-details-grid__wide"><span>ID raportów</span><strong>{report.reportIds?.length ? report.reportIds.join(', ') : '-'}</strong></div>
                    </div>
                )}

                {activeTab === 'message' && (
                    <div className="message-report-message-view">
                        <div className="message-report-message-list">
                            {messages.length === 0 && <p className="message-report-empty">Brak wiadomości w tym zgłoszeniu.</p>}
                            {messages.map((item) => (
                                <button
                                    type="button"
                                    key={item.messageId || item.reportId}
                                    className={selectedMessage?.messageId === item.messageId ? 'is-selected' : ''}
                                    onClick={() => setSelectedMessageId(item.messageId)}
                                >
                                    <span>{item.messageId}</span>
                                    <small>{item.messageAvailable === false ? 'Niedostępna' : 'Dostępna'}</small>
                                </button>
                            ))}
                        </div>
                        <div className="message-report-message-card">
                            <span className="message-report-modal__eyebrow">Treść wiadomości</span>
                            <p>{valueOrDash(message?.text || message?.messageContent)}</p>
                            <div className="message-report-message-meta">
                                <span>Nadawca: {valueOrDash(message?.senderId)}</span>
                                <span>Typ: {valueOrDash(message?.messageType)}</span>
                                <span>Data: {formatDate(message?.createdAt)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'chat' && (
                    <div className="message-report-details-grid">
                        <div><span>Nazwa czatu</span><strong>{valueOrDash(report.bunch?.name)}</strong></div>
                        <div><span>ID czatu</span><strong>{valueOrDash(report.bunchId)}</strong></div>
                        <div><span>Liczba uczestników</span><strong>{valueOrDash(report.bunch?.numberOfParticipants)}</strong></div>
                        <div><span>Chat publiczny</span><strong>{report.bunch?.isPublic === null || report.bunch?.isPublic === undefined ? '-' : report.bunch.isPublic ? 'Tak' : 'Nie'}</strong></div>
                        <div><span>ID wydarzenia</span><strong>{valueOrDash(report.bunch?.eventId)}</strong></div>
                        <div><span>Pierwsze zgłoszenie</span><strong>{formatDate(report.firstReportedAt)}</strong></div>
                    </div>
                )}
            </div>

            <div className="message-report-modal__footer">
                <button type="button" className="message-report-modal__secondary" onClick={onClose}>Zamknij</button>
                {canResolve && (
                    <button type="button" className="message-report-modal__resolve" onClick={() => onResolve(report)} disabled={resolving}>
                        {resolving ? 'Rozwiązywanie...' : 'Oznacz jako rozwiązane'}
                    </button>
                )}
            </div>
        </Modal>
    );
}

MessageReportDetailsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    report: PropTypes.object,
    initialTab: PropTypes.oneOf(['report', 'message', 'chat']),
    onClose: PropTypes.func.isRequired,
    onResolve: PropTypes.func.isRequired,
    resolving: PropTypes.bool,
};

MessageReportDetailsModal.defaultProps = {
    report: null,
    initialTab: 'report',
    resolving: false,
};

export default MessageReportDetailsModal;
